/**
 * computeBookingWindow
 * --------------------
 * Single source of truth for converting an array of services on a booking
 * into the wall-clock window that should be reserved on the diary.
 *
 * A booking is built up of:
 *   [ setup_first ][ svc1 ][ proc1 ][ fin1 ][ svc2 ][ proc2 ][ fin2 ]…[ cleanup_last ]
 *
 * finishingTime is hands-on time AFTER a service's processing (apply colour →
 * processing, stylist free → finish). Processing is a gap whenever hands-on
 * time follows it: the next service, or the service's own finishing. With no
 * finishing, the last service's processing is dropped (the client leaves).
 * Mirrors the backend's lib/module/v2/booking/serviceTiming.js.
 *
 * The multi-service rule (matches Vagaro and what most salons want):
 * - Only the FIRST service's setup time is honoured at the start.
 * - Only the LAST service's cleanup time is honoured at the end.
 * - Service-to-service transitions don't get individual setup/cleanup buffers
 *   (their `processingTime` already covers any developing-time gap).
 *
 * Per-appointment override rule:
 * - If the booked-service entry carries its own setupTime/cleanupTime/
 *   processingTime, those win.
 * - Otherwise we fall back to the service definition (the service object's
 *   own setupTime/cleanupTime/processingTime).
 *
 * @param {Array<object>} services - Each entry is one service on the booking.
 *   Recognised fields (any are optional, default to 0):
 *     - serviceTime / duration       (minutes)
 *     - setupTime                    (minutes, only first svc honoured)
 *     - cleanupTime                  (minutes, only last svc honoured)
 *     - processingTime               (minutes, between services, or before finishing)
 *     - finishingTime                (minutes, hands-on after processing)
 *
 * @returns {{
 *   setupMinutes: number,
 *   cleanupMinutes: number,
 *   totalServiceMinutes: number,     // sum of serviceTime across all services
 *   totalProcessingMinutes: number,  // processing that is a gap (not on last unless it has finishing)
 *   totalFinishingMinutes: number,   // sum of finishingTime
 *   totalSlotMinutes: number,        // setup + service + processing + finishing + cleanup
 * }}
 */
export function computeBookingWindow(services) {
  if (!Array.isArray(services) || services.length === 0) {
    return {
      setupMinutes: 0,
      cleanupMinutes: 0,
      totalServiceMinutes: 0,
      totalProcessingMinutes: 0,
      totalFinishingMinutes: 0,
      totalSlotMinutes: 0,
    };
  }

  // Use first service's setup, last service's cleanup.
  const first = services[0] || {};
  const last = services[services.length - 1] || {};
  const setupMinutes = toInt(first.setupTime);
  const cleanupMinutes = toInt(last.cleanupTime);

  let totalServiceMinutes = 0;
  let totalProcessingMinutes = 0;
  let totalFinishingMinutes = 0;

  services.forEach((s, idx) => {
    // serviceTime is the canonical name; some places use `duration`.
    totalServiceMinutes += toInt(s.serviceTime ?? s.duration);
    totalProcessingMinutes += effectiveProcessing(services, idx);
    totalFinishingMinutes += toInt(s.finishingTime);
  });

  const totalSlotMinutes =
    setupMinutes + totalServiceMinutes + totalProcessingMinutes + totalFinishingMinutes + cleanupMinutes;

  return {
    setupMinutes,
    cleanupMinutes,
    totalServiceMinutes,
    totalProcessingMinutes,
    totalFinishingMinutes,
    totalSlotMinutes,
  };
}

const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * Processing that is a real gap for the service at `idx`: always between
 * services; on the last service only when finishing follows it.
 */
export function effectiveProcessing(services, idx) {
  const s = services[idx] || {};
  const isLast = idx === services.length - 1;
  if (isLast && toInt(s.finishingTime) === 0) return 0;
  return toInt(s.processingTime);
}

/**
 * The diary blocks a booking is drawn as, in order: one 'service' block per
 * service plus a 'finishing' block for each service with finishingTime.
 * Shared by the diary renderer (normalizeData) and the drag/resize save
 * (BookingDetailPanel) so the two always agree on which block is which.
 * @returns {{ serviceIndex: number, role: 'service'|'finishing' }[]}
 */
export function bookingSegments(services) {
  const plan = [];
  (services || []).forEach((s, i) => {
    plan.push({ serviceIndex: i, role: 'service' });
    if (toInt(s && s.finishingTime) > 0) plan.push({ serviceIndex: i, role: 'finishing' });
  });
  return plan;
}

/**
 * Write dragged / resized diary block positions back onto the services
 * (mutates `services`). `segs` are the booking's blocks in bookingSegments
 * order, each { startMinutes, endMinutes, setupMinutes?, cleanupMinutes? }.
 *
 * - Processing = the gap after a service block (to its finishing, or to the
 *   next service). Nothing after the last block.
 * - A booking has no gap between a finish and the next service, so if the
 *   next service was dragged later, that wait goes into this service's
 *   processing (the finish moves with it) — otherwise the save would
 *   silently snap the next service back.
 * - On resize, block lengths become serviceTime / finishingTime. The first /
 *   last block is drawn widened by the setup / cleanup hatching, which is
 *   stripped so a resize doesn't fold buffers into the time.
 */
export function applySegmentPositions(services, segs, isResize) {
  const plan = bookingSegments(services);
  plan.forEach((p, k) => {
    const s = services[p.serviceIndex];
    const blockMinutes = segs[k].endMinutes - segs[k].startMinutes
      - (segs[k].setupMinutes || 0) - (segs[k].cleanupMinutes || 0);
    const gapAfter = k < plan.length - 1
      ? Math.max(0, segs[k + 1].startMinutes - segs[k].endMinutes)
      : 0;
    if (p.role === 'finishing') {
      if (isResize) s.finishingTime = Math.max(5, blockMinutes);
      if (gapAfter > 0) s.processingTime = (s.processingTime || 0) + gapAfter;
      return;
    }
    if (isResize) s.serviceTime = Math.max(5, blockMinutes);
    s.processingTime = gapAfter;
  });
  return services;
}

/**
 * Minutes the CLIENT is at the salon for an online basket: services +
 * processing gaps + finishing, i.e. the booking minus setup / cleanup (those
 * are internal buffers, never shown to clients). Timings come from the chosen
 * stylist's own copy of each service (service.hairdressers[]); the salon-level
 * fields are a roll-up that is often 0.
 */
export function clientVisitMinutes(services, stylistId) {
  const resolved = (services || []).map((s) => {
    const h = stylistId && Array.isArray(s.hairdressers)
      ? s.hairdressers.find((x) => x._id === stylistId)
      : null;
    const pick = (key, fallback) => {
      const v = h && h[key];
      return v !== undefined && v !== null ? v : fallback;
    };
    return {
      serviceTime: pick('serviceTime', parseInt(s.serviceTime ?? s.duration, 10) || 0),
      processingTime: pick('processingTime', s.processingTime),
      finishingTime: pick('finishingTime', s.finishingTime),
    };
  });
  const w = computeBookingWindow(resolved);
  return w.totalServiceMinutes + w.totalProcessingMinutes + w.totalFinishingMinutes;
}

export default computeBookingWindow;
